import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Redirect } from 'react-router';
import { useConfig } from '../../config';
import { useCriteo } from '../../hooks/useCriteo';
import { useTranslation } from '../../i18n/i18n';
import { buildHomeJsonLd } from '../../seo/homeJsonLd';
import { Res, useCms } from '../../state/api';
import { useSession } from '../../state/session';
import { CmsComponent } from '../cms/CmsComponent';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';
import { fetchCmsData } from './CmsPage';
import { LoaderPage } from './LoaderPage';

export const HomePage = (): JSX.Element => {
	const config = useConfig();
	const canonicalUrl = `https://${config.domain}/`;
	const { data, error } = useCms(Res.cmsPage, { slug: 'home' });
	const { sendCriteoEvent } = useCriteo();
	const { user } = useSession();
	const { routes } = useTranslation();

	useEffect(() => {
		sendCriteoEvent({ event: 'viewHome', tms: 'gtm-criteo-2.0.0' });
	}, []);

	// the site and the company that runs it do not depend on the cms, so they are published beside
	// a page that is still loading or failed to load. A cms failure on the server never reaches
	// here - the preload throws and the request becomes a 503 - so this is the client's path
	const jsonLd = buildHomeJsonLd(config);
	const head = (
		<Helmet>
			<link rel="canonical" href={canonicalUrl} />
			{jsonLd && <script type="application/ld+json">{jsonLd}</script>}
		</Helmet>
	);

	if (user?.specialsOnly) return <Redirect to={routes.specialPrices} />;
	if (error)
		return (
			<>
				{head}
				<ErrorMessage />
			</>
		);
	if (!data)
		return (
			<>
				{head}
				<LoaderPage />
			</>
		);

	return (
		<Main>
			{head}
			<div className="cms-page">
				<CmsComponent structure={data} />
			</div>
		</Main>
	);
};

export const homeRouting: Routing = {
	path(_urls) {
		return '/';
	},
	component: HomePage,
	requiresAuth: false,
	preload: async (config, _routeMatch, _query) => {
		return await fetchCmsData(config, 'home');
	},
};
